import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import type { ThemeLevel } from '@/components/admin/experiences/theme/experience-theme-panel';
import { ExperienceThemePanel } from '@/components/admin/experiences/theme/experience-theme-panel';
import {
  THEME_COLORS,
  backgroundSummary,
  buttonsSummary,
  clearedTheme,
  contrastWarnings,
  groupOverridden,
  motionSummary,
  themeInForce,
  typographySummary,
} from '@/lib/experience-theme';
import type { ExperienceTheme } from '@matter_extractor/shared';
import { Button, Icon, Tooltip } from '@pms-digital/design-system';
import { type ReactNode, useState } from 'react';

/**
 * La barre « Visuel global », repliée puis dépliée (refonte modulaire §9.4 ;
 * maquettes `Timeline.dc.html` et `BarreDeTheme.dc.html` ; issue #57).
 *
 * Collapsed it is one line of summaries per group and a « Régler »; open it is
 * the five columns of `ExperienceThemePanel`. Under both sits the contrast
 * warning of §9.4, named pair by pair.
 *
 * ## It is MOUNTABLE at two levels, and that is a decision of this lot
 *
 * §9.4 gives the same bar a second state - « dans l'éditeur de page, la barre
 * est celle de la page » - and the page editor is issue #58. So the bar takes
 * `level`, `value` and `onChange` rather than reaching for an `ExperienceDraft`:
 * the Timeline mounts it on `draft.theme` with no `inherited`, the page editor
 * mounts it on `page.visual` with the experience's theme as `inherited`, and
 * neither host has to know what the other does. A bar that had taken the draft
 * would have had to be forked for the second surface.
 *
 * ## Open or closed is the BAR's state, and nothing else's
 *
 * « Repliée ... et s'ouvre par "Régler" » - so the resting state is closed, and
 * whether somebody opened it is not a property of the experience. It does not
 * belong in the draft (an operator who opens the bar has not modified anything,
 * the arbitrage `protocol-timeline.tsx` makes for its zoom) and it does not
 * belong in the URL either: §9.1 puts the tab and the page there, and nothing
 * else. A capture script that wants the open panel presses « Régler », like
 * anybody: an opening the code could not reach by the gesture would be an
 * opening nothing tests.
 *
 * ## The contrast is shown in BOTH states
 *
 * It is drawn beside « Régler » when the bar is closed, which is the only place
 * an operator who never opens the panel would ever read it - and §9.4 wants it
 * read: « nommer la paire plutôt que signaler "contraste insuffisant" est ce qui
 * rend l'avertissement actionnable ». The save box repeats the very same
 * sentences, from the very same function.
 */

export interface ExperienceThemeBarProps {
  /** `global` - the experience's theme; `page` - one page's overrides (§9.4). */
  readonly level: ThemeLevel;
  /** What this level stores. Partial: `{}` is « rien de surchargé ». */
  readonly value: ExperienceTheme;
  /** The level below, at `page`. Absent at `global`, where it is the PMS default. */
  readonly inherited?: ExperienceTheme | undefined;
  /** « Un mot d'accueil » - what the head names after « Page · ». Ignored at `global`. */
  readonly subject?: string | undefined;
  readonly readOnly: boolean;
  readonly onChange: (next: ExperienceTheme) => void;
}

/** One cell of the collapsed row: the group's name, then what it is worth. */
function Summary({
  label,
  children,
  overridden,
  level,
}: {
  label: string;
  children: ReactNode;
  overridden: boolean;
  level: ThemeLevel;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <p className="flex items-center gap-1.5 font-semibold text-gray-600 text-caption uppercase tracking-wide">
        {label}
        {level === 'page' && overridden ? (
          <span className="rounded-md bg-bleu-nuit-tint px-1.5 py-0.5 font-semibold text-bleu-nuit text-caption normal-case tracking-normal">
            surchargé
          </span>
        ) : null}
      </p>
      {children}
    </div>
  );
}

export function ExperienceThemeBar({
  level,
  value,
  inherited,
  subject,
  readOnly,
  onChange,
}: ExperienceThemeBarProps) {
  const [open, setOpen] = useState(false);
  const force = themeInForce(value, inherited);
  const warnings = contrastWarnings(inherited ?? {}, value);

  const title = level === 'global' ? 'Visuel global' : `Page · ${subject ?? 'sans nom'}`;
  const subtitle =
    level === 'global'
      ? 'Rien de sélectionné : le thème de l’expérience'
      : 'Hérite du visuel global ; un token absent suit le thème.';

  return (
    <section
      data-testid={EXPERIENCE_IDS.themeBar}
      data-level={level}
      data-open={open ? 'true' : 'false'}
      className="space-y-3 rounded-md border-2 border-gray-200 bg-white p-4"
    >
      <div
        data-testid={EXPERIENCE_IDS.themeSummary}
        className="flex flex-wrap items-start gap-x-6 gap-y-3 text-small"
      >
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 className="font-semibold text-bleu-nuit text-body-lg leading-7">{title}</h2>
          <p className="text-gray-600 text-caption leading-4">{subtitle}</p>
        </div>

        <Summary
          label="Typographie"
          level={level}
          overridden={groupOverridden(value, ['titleFont', 'textFont', 'textScale'])}
        >
          <p className="text-gray-800 text-caption leading-4">{typographySummary(force)}</p>
        </Summary>

        <Summary
          label="Couleurs"
          level={level}
          overridden={groupOverridden(
            value,
            THEME_COLORS.map((one) => one.token),
          )}
        >
          <div className="flex flex-wrap items-center gap-1.5">
            {THEME_COLORS.map(({ token, label }) => (
              <Tooltip key={token} content={`${label} ${force[token]}`}>
                <span
                  // The one place a value an operator chose is PAINTED in the bar:
                  // eight names would be a column, eight discs are a glance.
                  style={{ backgroundColor: force[token] }}
                  className="block size-4 shrink-0 rounded-full border border-gray-200"
                >
                  {/*
                    Le nom, en texte MASQUÉ et pas en attribut.

                    Ce disque n'a aucun enfant textuel : son `title` natif était
                    donc son SEUL nom accessible, et la bulle dessinée ne le
                    remplace pas - elle ne s'expose que par `aria-describedby`,
                    et seulement pendant qu'elle est ouverte. Sans cette ligne,
                    les huit pastilles seraient muettes pour un lecteur d'écran,
                    ce qu'une revue a rattrapé. C'est l'idiome que le persona
                    utilise déjà pour ses crans et ses tons.
                  */}
                  <span className="sr-only">{`${label} ${force[token]}`}</span>
                </span>
              </Tooltip>
            ))}
          </div>
        </Summary>

        <Summary
          label="Fond"
          level={level}
          overridden={groupOverridden(value, [
            'backgroundKind',
            'backgroundImageAssetId',
            'backgroundVeil',
          ])}
        >
          <p className="text-gray-800 text-caption leading-4">{backgroundSummary(force)}</p>
        </Summary>

        <Summary
          label="Boutons"
          level={level}
          overridden={groupOverridden(value, ['buttonShape', 'buttonSize', 'buttonStyle'])}
        >
          <p className="text-gray-800 text-caption leading-4">{buttonsSummary(force)}</p>
        </Summary>

        <Summary
          label="Mouvement"
          level={level}
          overridden={groupOverridden(value, [
            'blockAnimation',
            'blockSpeed',
            'pageTransition',
            'pageTransitionSpeed',
            'reduceMotion',
          ])}
        >
          <p className="max-w-[36ch] text-gray-800 text-caption leading-4">
            {motionSummary(force)}
          </p>
        </Summary>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {level === 'page' && !readOnly ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid={EXPERIENCE_IDS.themeReset}
              onClick={() => onChange(clearedTheme())}
            >
              Revenir au global
            </Button>
          ) : null}
          <Button
            type="button"
            variant="accent"
            size="sm"
            icon="tune"
            data-testid={EXPERIENCE_IDS.themeToggle}
            aria-expanded={open}
            onClick={() => setOpen((was) => !was)}
          >
            {open ? 'Replier' : 'Régler'}
          </Button>
        </div>
      </div>

      {open ? (
        <ExperienceThemePanel
          level={level}
          value={value}
          inherited={inherited}
          readOnly={readOnly}
          onChange={onChange}
        />
      ) : null}

      {warnings.length === 0 ? null : (
        <ul data-testid={EXPERIENCE_IDS.themeContrast} className="space-y-1">
          {warnings.map((sentence) => (
            <li
              key={sentence}
              className="flex items-start gap-1.5 text-jaune-deep text-caption leading-4"
            >
              <span className="mt-px inline-flex shrink-0">
                <Icon name="warning" size={14} />
              </span>
              <span>{sentence}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
